import { action, observable } from "mobx";

import apiCaller from "@client/utils/ApiCaller";
import { Licence, LicenceInput } from "@typings/Licence";

class LicenceStore {
  @observable loading = false;
  @observable updating = false;
  @observable licences: Licence[] = [];
  @observable generatedPem = "";
  @observable generating = false;
  @observable sending = false;

  @action async load(opts?: {
    page?: number;
    limit?: number;
    forceShowLoading?: boolean;
    hideLoading?: boolean;
  }): Promise<void> {
    this.loading = this.licences.length === 0;

    if (opts) {
      if (opts.hideLoading) this.loading = false;
      if (opts.forceShowLoading) this.loading = true;
      if (opts.page && opts.limit) this.loading = true;
    }
    try {
      const licences: Licence[] = await apiCaller.send({ url: "/licences" });
      this.loadSuccess(licences);
    } catch (err) {
      this.loadFailed();
    }
  }

  @action loadSuccess(licences: Licence[]) {
    this.loading = false;
    this.licences = licences;
  }

  @action loadFailed() {
    this.loading = false;
  }

  @action async add(licence: LicenceInput): Promise<Licence | null> {
    this.updating = true;
    try {
      const {
        newLicence,
        pem,
      }: {
        newLicence: Licence;
        pem: string;
      } = await apiCaller.send({
        url: "/licences",
        method: "POST",
        data: licence,
      });
      this.addSuccess(newLicence);
      this.generateSuccess(pem);
      return newLicence;
    } catch (err) {
      this.addFailed();
      return null;
    }
  }

  @action addSuccess(licence: Licence) {
    this.updating = false;
    this.licences = [licence, ...this.licences];
  }

  @action addFailed() {
    this.updating = false;
  }

  @action async generate(licence: Licence): Promise<boolean> {
    this.generating = true;
    this.generatedPem = "";
    try {
      const pem: string = await apiCaller.send({
        url: `/licences/${licence.id}/pem`,
        method: "GET",
        data: licence,
      });
      this.generateSuccess(pem);
      return true;
    } catch (err) {
      this.generateFailed();
      return false;
    }
  }

  @action generateSuccess(pem: string) {
    this.generating = false;
    this.generatedPem = pem;
  }

  @action generateFailed() {
    this.generating = false;
  }

  @action async send(licenceId: string): Promise<boolean> {
    this.sending = true;
    try {
      await apiCaller.send({ url: `/licences/${licenceId}/email` });
      this.sendSuccess();
      return true;
    } catch (err) {
      this.sendFailed();
      return false;
    }
  }

  @action sendSuccess() {
    this.sending = false;
  }

  @action sendFailed() {
    this.sending = false;
  }

  async delete(licenceId: string) {
    await apiCaller.send({
      url: `/licences/${licenceId}`,
      method: "DELETE",
    });
    this.deleteSuccess(licenceId);
  }

  @action deleteSuccess(licenceId: string) {
    this.licences = this.licences.filter(licence => licence.id !== licenceId);
  }
}

export default new LicenceStore();
